import Vue from 'vue';
import Router from 'vue-router';
import LoginForm from './components/Login.vue';
import DashboardLayout from './components/Dashboard.vue';
import PlatformLayout from './layouts/PlatformLayout.vue';
import ChangePasswordView from './views/ChangePasswordView.vue';
import PlatformDashboardView from './views/platform/PlatformDashboardView.vue';
import BusinessListView from './views/platform/BusinessListView.vue';
import BusinessCreateView from './views/platform/BusinessCreateView.vue';
import BusinessDetailView from './views/platform/BusinessDetailView.vue';
import AdminDashboardView from './views/admin/AdminDashboardView.vue';
import ProductAdminView from './views/admin/ProductAdminView.vue';
import CategoryAdminView from './views/admin/CategoryAdminView.vue';
import InventoryManagementView from './views/admin/InventoryManagementView.vue';
import CashClosureView from './views/admin/CashClosureView.vue';
import ClientAdminView from './views/admin/ClientAdminView.vue';
import SalesTicketsView from './views/admin/SalesTicketsView.vue';
import ReportsSalesSectionView from './views/admin/reports/ReportsSalesSectionView.vue';
import ReportsInventorySectionView from './views/admin/reports/ReportsInventorySectionView.vue';
import ReportsClientsSectionView from './views/admin/reports/ReportsClientsSectionView.vue';
import InventorySalesProductsReportView from './views/admin/reports/InventorySalesProductsReportView.vue';
import InventoryExistenceReportView from './views/admin/reports/InventoryExistenceReportView.vue';
import SalesSummaryReportView from './views/admin/reports/SalesSummaryReportView.vue';
import SalesTicketsReportView from './views/admin/reports/SalesTicketsReportView.vue';
import SalesProfitReportView from './views/admin/reports/SalesProfitReportView.vue';
import OperatorSelectView from './views/OperatorSelectView.vue';
import { canAccessOperatorPath } from './utils/operatorPermissions';
import { validateOperatorSession } from './services/posOperatorService';

Vue.use(Router);

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginForm
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePasswordView,
    meta: { requiresAuth: true }
  },
  {
    path: '/operator-select',
    name: 'OperatorSelect',
    component: OperatorSelectView,
    meta: { requiresAuth: true, operatorSetup: true }
  },
  {
    path: '/platform',
    component: PlatformLayout,
    meta: { requiresAuth: true, scope: 'platform' },
    children: [
      {
        path: '',
        redirect: '/platform/dashboard'
      },
      {
        path: '/platform/dashboard',
        name: 'PlatformDashboard',
        component: PlatformDashboardView
      },
      {
        path: '/platform/system',
        name: 'PlatformSystem',
        component: () => import('./views/platform/PlatformSystemView.vue')
      },
      {
        path: '/platform/businesses',
        name: 'PlatformBusinesses',
        component: BusinessListView
      },
      {
        path: '/platform/businesses/new',
        name: 'PlatformBusinessCreate',
        component: BusinessCreateView
      },
      {
        path: '/platform/businesses/:id',
        name: 'PlatformBusinessDetail',
        component: BusinessDetailView
      },
    ]
  },
  {
    // Ruta layout principal para usuarios autenticados
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true, scope: 'tenant' },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard'
      },
      {
        path: '/admin/dashboard',
        name: 'AdminDashboard',
        component: AdminDashboardView
      },
      {
        path: '/unauthorized',
        name: 'Unauthorized',
        component: () => import('./views/UnauthorizedView.vue')
      },
      {
        path: '/admin/cash-closure',
        name: 'AdminCashClosure',
        component: CashClosureView
      },
      {
        path: '/admin/sales',
        name: 'AdminSales',
        component: SalesTicketsView
      },
      {
        path: '/admin/sales/tickets/:receiptId/operations/:mode',
        name: 'AdminTicketOperations',
        component: () => import('./views/admin/TicketOperationsView.vue')
      },
      {
        path: '/admin/pos',
        name: 'AdminPosSales',
        component: () => import('./views/admin/PosSalesView.vue')
      },
      {
        path: '/admin/quotations',
        name: 'AdminQuotations',
        component: () => import('./views/admin/QuotationsView.vue')
      },
      {
        path: '/admin/quotations/new',
        name: 'AdminQuotationNew',
        component: () => import('./views/admin/QuotationEditorView.vue')
      },
      {
        path: '/admin/quotations/:id/edit',
        name: 'AdminQuotationEdit',
        component: () => import('./views/admin/QuotationEditorView.vue')
      },
      {
        path: '/admin/promotions',
        name: 'AdminPromotions',
        component: () => import('./views/admin/PromotionsView.vue')
      },
      {
        path: '/admin/print-bridges',
        name: 'AdminPrintBridges',
        component: () => import('./views/admin/PrintBridgesView.vue')
      },
      {
        path: '/admin/settings',
        name: 'AdminSettings',
        component: () => import('./views/admin/SettingsView.vue')
      },

      {
        path: '/admin/products',
        name: 'AdminProducts',
        component: ProductAdminView
      },
      {
        path: '/admin/categories',
        name: 'AdminCategories',
        component: CategoryAdminView
      },
      {
        path: '/admin/inventory',
        name: 'AdminInventory',
        component: InventoryManagementView
      },
      {
        path: '/admin/clients',
        name: 'AdminClients',
        component: ClientAdminView
      },
      {
        path: '/admin/clients/new',
        name: 'AdminClientCreate',
        component: () => import('./views/admin/ClientFormView.vue')
      },
      {
        path: '/admin/clients/edit/:id',
        name: 'AdminClientEdit',
        component: () => import('./views/admin/ClientFormView.vue')
      },
      {
        path: '/admin/customer-history',
        name: 'AdminCustomerHistory',
        component: () => import('./views/admin/CustomerHistoryView.vue')
      },
      {
        path: '/admin/customer-history/:customerId',
        name: 'AdminCustomerHistoryDetail',
        component: () => import('./views/admin/CustomerHistoryDetailView.vue')
      },
      {
        path: '/admin/products/new',
        name: 'AdminProductCreate',
        component: () => import('./views/admin/ProductFormView.vue')
      },
      {
        path: '/admin/products/edit/:id',
        name: 'AdminProductEdit',
        component: () => import('./views/admin/ProductFormView.vue')
      },
      {
        path: '/admin/users',
        name: 'AdminUsers',
        component: () => import(/* webpackChunkName: "admin-users" */ './components/admin/UserAdmin.vue')
      },
      {
        path: '/admin/roles-permissions',
        name: 'AdminRolesPermissions',
        component: () => import('./views/admin/RolesPermissionsView.vue')
      },
      {
        path: '/admin/pos-users',
        name: 'AdminPosUsers',
        component: () => import('./views/admin/PosUsersView.vue')
      },
      {
        path: '/admin/reports',
        redirect: '/admin/reports/inventory'
      },
      {
        path: '/admin/reports/sales',
        name: 'AdminReportsSales',
        component: ReportsSalesSectionView
      },
      {
        path: '/admin/reports/sales/summary',
        name: 'AdminReportSalesSummary',
        component: SalesSummaryReportView
      },
      {
        path: '/admin/reports/sales/tickets',
        name: 'AdminReportSalesTickets',
        component: SalesTicketsReportView
      },
      {
        path: '/admin/reports/sales/profit',
        name: 'AdminReportSalesProfit',
        component: SalesProfitReportView
      },
      {
        path: '/admin/reports/inventory',
        name: 'AdminReportsInventory',
        component: ReportsInventorySectionView
      },
      {
        path: '/admin/reports/clients',
        name: 'AdminReportsClients',
        component: ReportsClientsSectionView
      },
      {
        path: '/admin/reports/inventory/products-sales',
        name: 'AdminReportInventorySalesProducts',
        component: InventorySalesProductsReportView
      },
      {
        path: '/admin/reports/legacy/:reportId',
        name: 'AdminLegacyReportViewer',
        component: () => import('./views/admin/reports/LegacyReportViewerView.vue')
      },
      {
        path: '/admin/reports/inventory/existence',
        name: 'AdminReportInventoryExistence',
        component: InventoryExistenceReportView
      },
    ]
  },
  {
    path: '/',
    redirect: () => {
      const token = localStorage.getItem('token');
      if (!token) return '/login';
      if (localStorage.getItem('mustChangePassword') === 'true') return '/change-password';
      if (localStorage.getItem('userRole') === 'super_admin') return '/platform/dashboard';
      return sessionStorage.getItem('posOperatorToken') ? '/dashboard' : '/operator-select';
    }
  },
  {
    // Captura cualquier ruta no definida
    path: '*',
    redirect: () => {
      const token = localStorage.getItem('token');
      if (!token) return '/login';
      const role = localStorage.getItem('userRole');
      if (localStorage.getItem('mustChangePassword') === 'true') return '/change-password';
      if (role === 'super_admin') return '/platform/dashboard';
      return sessionStorage.getItem('posOperatorToken') ? '/dashboard' : '/operator-select';
    }
  }
];

const getPostLoginRoute = () => {
  const role = localStorage.getItem('userRole');
  if (localStorage.getItem('mustChangePassword') === 'true') return '/change-password';
  if (role === 'super_admin') return '/platform/dashboard';
  return sessionStorage.getItem('posOperatorToken') ? '/dashboard' : '/operator-select';
};

const router = new Router({
  mode: 'history',
  routes
});

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('userRole');
  const mustChangePassword = localStorage.getItem('mustChangePassword') === 'true';
  const operatorToken = sessionStorage.getItem('posOperatorToken');

  if (to.matched.some(record => record.meta.requiresAuth) && !token) {
    return next('/login');
  }

  if (token && mustChangePassword && to.path !== '/change-password' && to.path !== '/login') {
    return next('/change-password');
  }

  if (token && to.path === '/login') {
    return next(getPostLoginRoute());
  }

  if (role === 'super_admin' && to.path.startsWith('/admin')) {
    return next('/platform/dashboard');
  }

  if (role === 'super_admin' && to.path === '/operator-select') {
    return next('/platform/dashboard');
  }

  if (role === 'super_admin' && to.path === '/dashboard') {
    return next('/platform/dashboard');
  }

  if (role !== 'super_admin' && to.matched.some(record => record.meta.scope === 'platform')) {
    return next('/dashboard');
  }

  const tenantRoute = to.matched.some(record => record.meta.scope === 'tenant');
  if (
    token
    && role !== 'super_admin'
    && !mustChangePassword
    && tenantRoute
    && !operatorToken
  ) {
    return next('/operator-select');
  }

  if (token && role !== 'super_admin' && operatorToken && to.path === '/operator-select') {
    return next('/dashboard');
  }

  if (
    token
    && role !== 'super_admin'
    && operatorToken
    && tenantRoute
    && to.path !== '/unauthorized'
  ) {
    try {
      const session = await validateOperatorSession();
      if (!session) return next('/operator-select');
    } catch (_error) {
      // Un 401 limpia el token en el interceptor; un fallo de red conserva la
      // sesión local para no expulsar al operador por una interrupción breve.
      if (!sessionStorage.getItem('posOperatorToken')) return next('/operator-select');
    }
  }

  if (
    token
    && role !== 'super_admin'
    && operatorToken
    && tenantRoute
    && to.path !== '/unauthorized'
    && !canAccessOperatorPath(to.path)
  ) {
    return next('/unauthorized');
  }

  return next();
});

export default router; 